import { html, unsafeCSS } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { keyed } from 'lit/directives/keyed.js';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { PopBaseComponent } from '../../base/pop-base-component';
import { isNullish } from '../../common/utils/nullish';
import type { HoldingsTable } from './holdings-table.model';
import { Holding } from '../common/holding.model';
import { Constants } from './holdings-table.constants';
import { Constants as HoldingsConstants } from '../holdings/holdings.constants';
import { Constants as BaseConstants } from '../../base/base.constants.js';
import {
  formatNumberToShowSign,
  formatNumberToUSDCurrency,
  formatToNDecimalPlaces,
} from '../../common/utils/formatNumber';
import style from './holdings-table.scss?inline';
import { IconSVG } from '../../common/utils/icon';
import { SortDirection, SortDirections } from '../../common/utils/tableSort';
import { dateToMMDDYYYY } from '../../common/utils/formatDate';

/**
 * @tag pop-holdings-table-component
 * @property {HoldingsTable} [componentObject] Holdings table content
 * @attribute {HoldingsTable} [data-component-object] Holdings table content
 */
@customElement(Constants.TAG_NAME)
export class PopHoldingsTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   * `static readonly` was added here as a short term fix because without it,
   * the component is not rendering in Storybook
   */
  static readonly tagName = Constants.TAG_NAME;

  /**
   * The component object that contains the pre-fetched data to be rendered.
   */
  @property({
    attribute: BaseConstants.DATA_COMPONENT_OBJECT,
    type: Object,
  })
  componentObject?: HoldingsTable;

  /**
   * Dispatches a custom holdings sort event with the id of the column to sort by
   *
   * @param column The id of the column to sort by
   */
  private dispatchSort(column: string) {
    this.dispatchEvent(
      new CustomEvent(Constants.SORT_EVENT, {
        detail: {
          column,
        },
      }),
    );
  }

  /**
   * Takes a sort direction and returns a human readable label to be used for accessibility purposes
   * Default is for the sort button that doesn't show a current sorting direction
   *
   * @param direction The sort direction
   * @returns A human readable label to be used for accessibility purposes
   */
  private getSortButtonLabel(direction?: SortDirection) {
    switch (direction) {
      case SortDirections.ASCENDING:
        return Constants.ASCENDING_LABEL;
      case SortDirections.DESCENDING:
        return Constants.DESCENDING_LABEL;
      default:
        return Constants.SORT_LABEL;
    }
  }

  /**
   * Renders the correct sort button for the specified column and current sort state
   *
   * @param obj Includes the column the button is for and the current sortState so it can
   * determine the correct icon to display
   *
   * @returns A sort button with the correct icon, accessible label, and click handler to
   * handle the sort event for the specified column
   */
  private renderSortButton({
    column,
    sortState,
  }: {
    column: string;
    sortState?: { column: string; direction: SortDirection };
  }) {
    const direction =
      sortState?.column === column ? sortState.direction : undefined;
    const sortButtonLabel = this.getSortButtonLabel(direction);

    // Need to use `keyed` to ensure that the aria label on the button updates when the sort direction changes
    // Currently the beacon-icon-button doesn't update the aria label when the slot content changes
    // `keyed` can be removed once this issue is fixed in the beacon-icon-button
    return keyed(
      sortButtonLabel,
      html`<beacon-icon-button
        variant="tertiary"
        slot="action-zone"
        @beacon-event-click="${() => {
          this.dispatchSort(column);
        }}"
        >${sortButtonLabel}
        ${this.renderSortButtonSVG(direction)}</beacon-icon-button
      >`,
    );
  }

  /**
   * Renders the correct icon given the sort direction to show
   *
   * @param direction The sort direction to show
   */
  private renderSortButtonSVG(direction?: SortDirection) {
    switch (direction) {
      case SortDirections.ASCENDING:
        //Needs to be update once the icon is added to the beacon icon library
        return IconSVG.sortedAscending({ slot: 'icon' });
      case SortDirections.DESCENDING:
        //Needs to be update once the icon is added to the beacon icon library
        return IconSVG.sortedDescending({ slot: 'icon' });
      default:
        return html`<beacon-icon slot="icon" name="sort"></beacon-icon>`;
    }
  }

  /**
   * Renders the table head for the holdings table component.
   * @returns {TemplateResult} The rendered table head.
   */
  private renderTableHead() {
    const columnsToShow = this.componentObject?.columnsToShow;
    if (!columnsToShow) {
      return html``;
    }

    const sortedColumn = this.componentObject?.sortState?.column;

    return html`
      <tr>
        ${columnsToShow.map((column) => {
          // Determine the class name for the column headers
          const classes = {
            [Constants.SORTED]: sortedColumn === column.id,
            [Constants.RIGHT_ALIGN]: column.rightAlign,
          };

          let ariaSort;
          if (sortedColumn === column.id) {
            ariaSort =
              this.componentObject?.sortState?.direction ===
              SortDirections.ASCENDING
                ? Constants.ASCENDING_ARIA_SORT_VALUE
                : Constants.DESCENDING_ARIA_SORT_VALUE;
          }

          // The content for the default slot needs the attribute `slot=""` in
          // order to be found in Serenity tests.

          return html`
            <th
              id="${column.id}"
              scope="col"
              aria-sort=${ifDefined(ariaSort)}
              class=${classMap(classes)}
            >
              <beacon-table-header>
                <span slot="">${column.label}</span>
                ${this.renderSortButton({
                  column: column.id,
                  sortState: this.componentObject?.sortState,
                })}</beacon-table-header
              >
            </th>
          `;
        })}
      </tr>
    `;
  }

  /**
   * Renders the table body for the holdings table component.
   * @returns {TemplateResult} The rendered table body.
   */
  private renderTableBody(holdings: Holding[]) {
    const columnsToShow = this.componentObject?.columnsToShow;
    if (holdings.length === 0 || !columnsToShow) {
      return html``;
    }

    const sortedColumn = this.componentObject?.sortState?.column;

    //Helper function to determine if a column should be rendered
    const getColumn = (id) => columnsToShow.find((column) => column.id === id);

    const holdingColumn = getColumn(HoldingsConstants.HOLDING_COLUMN_ID);
    const sectorColumn = getColumn(HoldingsConstants.SECTOR_COLUMN_ID);
    const industryColumn = getColumn(HoldingsConstants.INDUSTRY_COLUMN_ID);
    const marketValueColumn = getColumn(
      HoldingsConstants.MARKET_VALUE_COLUMN_ID,
    );
    const holdingMaturityDateColumn = getColumn(
      HoldingsConstants.HOLDING_MATURITY_DATE_COLUMN_ID,
    );
    const holdingCouponRateColumn = getColumn(
      HoldingsConstants.HOLDING_COUPON_RATE_COLUMN_ID,
    );
    const percentageTotalNetAssetsColumn = getColumn(
      HoldingsConstants.PERCENT_OF_PORTFOLIO_COLUMN_ID,
    );
    const percentageChangeColumn = getColumn(
      HoldingsConstants.PERCENT_CHANGE_COLUMN_ID,
    );

    //Helper function to render a column
    const renderColumn = (column, variant, value) =>
      column
        ? html` <td>
            <beacon-table-record
              variant="${variant}"
              ?sorted=${sortedColumn === column.id}
            >
              ${value}
            </beacon-table-record>
          </td>`
        : '';

    //Helper function to render the holding column
    const renderHoldingColumn = (row) =>
      holdingColumn
        ? html` <th scope="row">
            <beacon-table-stub>
              <span class="beacon-table-stub__category" slot="category">
                ${row.tickerSymbol}
              </span>
              <span
                class="b-typography-sans-serif--body--100 b-font-weight-bold"
              >
                ${row.name || Constants.NOT_APPLICABLE}</span
              >
            </beacon-table-stub>
          </th>`
        : '';

    return html`
      ${holdings.map(
        (row) => html`
          <tr>
            ${renderHoldingColumn(row)}
            ${renderColumn(
              sectorColumn,
              'text',
              row.sectorName || Constants.NOT_APPLICABLE,
            )}
            ${renderColumn(
              industryColumn,
              'text',
              row.industryName || Constants.NOT_APPLICABLE,
            )}
            ${renderColumn(
              marketValueColumn,
              'numeral',
              !isNullish(row.marketValue)
                ? formatNumberToUSDCurrency(row.marketValue)
                : Constants.NOT_APPLICABLE,
            )}
            ${renderColumn(
              holdingMaturityDateColumn,
              'text',
              !isNullish(row.holdingMaturityDate)
                ? dateToMMDDYYYY(row.holdingMaturityDate)
                : Constants.NOT_APPLICABLE,
            )}
            ${renderColumn(
              holdingCouponRateColumn,
              'numeral',
              !isNullish(row.holdingCouponRate)
                ? formatToNDecimalPlaces(row.holdingCouponRate, 2)
                : Constants.NOT_APPLICABLE,
            )}
            ${renderColumn(
              percentageTotalNetAssetsColumn,
              'numeral',
              !isNullish(row.percentageTotalNetAssets)
                ? formatToNDecimalPlaces(row.percentageTotalNetAssets, 2)
                : Constants.NOT_APPLICABLE,
            )}
            ${renderColumn(
              percentageChangeColumn,
              'numeral',
              !isNullish(row.percentageChange)
                ? formatNumberToShowSign(row.percentageChange)
                : Constants.NOT_APPLICABLE,
            )}
          </tr>
        `,
      )}
    `;
  }

  render() {
    const holdings = this.componentObject?.holdings || [];

    return this.renderComponent(html`
      <beacon-table variant="fixed">
        <table>
          <thead>
            ${this.renderTableHead()}
          </thead>
          <tbody>
            ${this.renderTableBody(holdings)}
          </tbody>
        </table>
      </beacon-table>
    `);
  }
}
